import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import hero from "./assets/DigitalGrowth/digital-growth-for-accountants-hero.png";
import value from "./assets/DigitalGrowth/accountant-seo-services-local-visibilit.png";
import process from "./assets/DigitalGrowth/seo-for-accounting-firms-foundation.png";
import sixStep from "./assets/DigitalGrowth/accounting-firm-online-growth-checklist.png";
import phone from "./assets/DigitalGrowth/attoli-free-growth-call.png";
import Footer from "./Footer";

const DigitalGrowth = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli helps accounting firms grow their online visibility and turn search traffic into new clients. Book a free call to see your growth plan today."
        />
        <title>Digital Growth for Accountants</title>
        <link
          rel="canonical"
          href="https://attoli.com/services/digital-growth-for-accountants"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title-growth">
                Digital Growth For{" "}
                <span className="hero-accent">Accountants</span>
              </h1>
              <p className="servicePage-description">
                Turn website visitors into real clients. Most accounting firms
                see steady growth in traffic and leads within the first few
                months once the right foundation is in place.
              </p>

              <ul className="trust-badges">
                <li>No locked in contracts</li>
                <li>Built only for accounting and bookkeeping firms</li>
                <li>Free site audit before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book My Free Growth Call
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Laptop showing a rising website traffic graph next to a small accounting office desk"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Happens When You Skip Accountant SEO Services
            </h2>
            <p className="body-copy">
              Most accounting firms build a website once and never touched it
              again. Meanwhile the firms that keep growing treat their website
              like a real part of the business. They publish regularly. They
              track. They improve. They build authority. The firms that don't
              stay stuck collecting dust.
            </p>
            <p className="body-copy">
              If your site has not changed in years Google notices. Search
              rankings favor firms that stay active. When traffic slows down
              fewer people find you and even fewer become clients.
            </p>
            <p className="body-copy">
              Without accountant seo services in place your firm stays stuck
              relying on referrals alone. Referrals are great but they only take
              a firm so far.
            </p>
            <p className="body-copy body-copy-emphasis">
              Growth online doesn't happen by accident. It happens with a clear
              plan and consistent work behind it.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Seo For Accounting Firms Actually Includes
            </h2>
            <p className="body-copy">
              Attoli builds and manages the digital growth work most accounting
              firms never get around to.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Website SEO Audit And Fixes</h3>
                <p>
                  We review your site for the technical issues holding back your
                  rankings and fix them. A clean site gives search engines a
                  reason to trust and rank you.
                </p>
              </div>
              <div className="value-card">
                <h3>Keyword Research And Targeting</h3>
                <p>
                  We find the exact terms potential clients search before hiring
                  an accountant. Your content gets built around real demand
                  instead of guesses.
                </p>
              </div>
              <div className="value-card">
                <h3>Content And Landing Page Creation</h3>
                <p>
                  We write and build pages around your services so your site
                  actually shows up for the searches that matter.
                </p>
              </div>
              <div className="value-card">
                <h3>On Page SEO Optimization</h3>
                <p>
                  We optimize titles headers and meta descriptions so every page
                  has the best chance to rank.
                </p>
              </div>
              <div className="value-card">
                <h3>Backlink And Authority Building</h3>
                <p>
                  We build your site's authority through quality links and
                  mentions. Authority is one of the biggest factors in how high
                  you rank.
                </p>
              </div>
              <div className="value-card">
                <h3>Monthly Growth Reporting</h3>
                <p>
                  We track your traffic rankings every month so you always know
                  what your investment is producing.
                </p>
              </div>
            </div>

            <img
              src={value}
              alt="Magnifying glass over a map pin with the text found first representing local search visibility"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              How We Increase Online Visibility For Your Firm
            </h2>
            <p className="body-copy">
              Growing your visibility online is not random. It starts with a
              clear foundation and builds from there.
            </p>
            <img
              src={process}
              alt="House diagram with a foundation labeled keyword research and floors labeled pages and content"
              className="inline-image"
            />

            <p className="body-copy">
              Think of it like building a house. Keyword research is the
              foundation. Pages and content are the floors built on top. Skip
              the foundation and the rest of the house has nothing solid to
              stand on.
            </p>
            <p className="body-copy">
              We start every project the same way. Research first. Structure
              second. Content third. That order is what keeps rankings solid
              instead of shaky.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Our Accounting Firm Online Growth Process Works Step By Step
            </h2>

            <img
              src={sixStep}
              alt="Six step checklist for accounting firm online growth covering setup research pages reviews tracking and reporting"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>How long until I see results?</summary>
              <p>
                Most accounting firms start seeing movement in rankings and
                traffic within 60 to 90 days. Full results usually build over 4
                to 6 months since digital growth compounds over time.
              </p>
            </details>
            <details className="faq-item">
              <summary>How do you get backlinks?</summary>
              <p>
                We do not buy backlinks! We build authority through quality
                content and outreach to other sites in your industry to swap
                links or reach out to reports that may have questions we fell
                your firm can answer.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>What if I already have a website?</summary>
              <p>
                That is fine. We work with your current site and build the
                growth work on top of it. No rebuild required. We also work with
                website builders like Wix, Squarespace, and WordPress.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I need to understand SEO myself?</summary>
              <p>
                No. We handle the strategy the setup and the monthly work. You
                get simple monthly reports that show exactly what changed and
                why.
              </p>
            </details>
          </div>
        </section>

        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To Grow Your Firm's Visibility Online?
            </h2>
            <p className="body-copy">
              Book a free 20 minute call. Together we will look at your current
              website performance and show you exactly where you are losing
              visitors and what I would change to get more of the right clients
              to your firm.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default DigitalGrowth;
